import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { getDeepCopy } from '@degreed/core/utils';
import { TranslateService } from '@ngx-translate/core';
import { AutomationValueLookupComponent } from '../../automation-value-lookup/automation-value-lookup.component';
import {
  AutomationComponentType,
  AutomationResource,
  EngageEvent,
  Trigger,
} from '../../model';
import { TriggerBackCancelSaveButtonsComponent } from '../trigger-back-cancel-save-buttons/trigger-back-cancel-save-buttons.component';

@Component({
  selector: 'dgx-trigger-single-attribute-value-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AutomationValueLookupComponent,
    TriggerBackCancelSaveButtonsComponent,
  ],
  templateUrl: './trigger-single-attribute-value-form.component.html',
})
export class TriggerSingleAttributeValueFormComponent implements OnInit {
  // Inputs
  public readonly trigger = input.required<Trigger>();
  public readonly orgId = input.required<number>();
  public readonly isSaving = input(false);
  public readonly events = input<EngageEvent[]>([]);

  // Outputs
  public readonly cancelEmitter = output<void>();
  public readonly saveEmitter = output<Trigger>();
  public readonly backEmitter = output<void>();

  private readonly translate = inject(TranslateService);

  public readonly isInvalid = signal(false);
  public i18n = this.translate.instant(['BusinessRules_AttributeValue']);
  public readonly i18nShared = this.translate.instant([
    'Core_NoResults',
    'Core_FieldRequired',
    'Core_Back',
    'Core_Save',
    'Core_Cancel',
    'Core_Search',
    'Core_Loading',
  ]);
  public localTrigger: Trigger;
  public readonly compType = AutomationComponentType.Trigger;
  public triggerForm: FormGroup;

  @ViewChildren('triggerInput')
  public searchFields: QueryList<AutomationValueLookupComponent>;

  public get resourceFieldName(): string {
    const triggerVal = this.trigger();
    const translatedConditionalLabelKey = `Automation_PredicateLabel_${triggerVal.userActivity?.replace(/ /g, '')}`;
    const translatedConditionalLabel = this.translate.instant(
      translatedConditionalLabelKey
    );
    return translatedConditionalLabel === translatedConditionalLabelKey
      ? this.i18n.BusinessRules_AttributeValue
      : translatedConditionalLabel;
  }

  public get resourceTypeForTrigger(): string {
    const triggerVal = this.trigger();
    const event = this.events().find(
      (x) => x.eventName === triggerVal.userActivity
    );
    return event.associatedResourceType;
  }

  public ngOnInit(): void {
    this.localTrigger = getDeepCopy(this.trigger());
    const formControlValue = this.trigger()?.displayResourceValue
      ? this.trigger().displayResourceValue
      : '';
    this.triggerForm = new FormGroup({
      triggerValue: new FormControl(formControlValue, [Validators.required]),
    });
  }

  public setTriggerSecondaryValue(
    resource?: AutomationResource | AutomationResource[]
  ) {
    // When isMultiSelect is false, the event always emits a single item
    if (Array.isArray(resource)) {
      resource = resource[0];
    }
    this.localTrigger.resourceValue = resource?.resourceId;
    this.localTrigger.resourceValueSecondary =
      resource?.resourceInputType ?? resource?.resourceType;
    this.localTrigger.displayResourceValue = resource?.name;
    const value = resource
      ? {
          name: resource?.name,
          resourceId: resource?.resourceId,
        }
      : null;
    this.triggerForm.get('triggerValue').setValue(value);
  }

  public onSave(): void {
    this.isInvalid.set(this.triggerForm.invalid);
    if (this.isInvalid()) {
      this.handleErrors();
      return;
    }
    this.saveEmitter.emit(this.localTrigger);
  }

  public onCancel(): void {
    this.cancelEmitter.emit();
  }

  public onBack(): void {
    this.backEmitter.emit();
  }

  public handleErrors(): void {
    setTimeout(() => {
      this.searchFields.first.searchInput.nativeElement.focus();
    });
  }
}
